import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Alert, Flex, Typography, Input, Button, Select, Divider, Image as AntImage } from 'antd';
import { PartitionOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import GreenLightButton from '../../../components/UI/Buttons/GreenLightButton';
import LspivService from '../../../services/LspivService';

const { Title, Text } = Typography;

const emptySection = () => ({
    east_l: 0, north_l: 0, east_r: 10, north_r: 0, rw_length: 10,
});

const SectionsStep = ({ jobId, onRefresh }) => {
    const [sections, setSections] = useState({ section_1: emptySection() });
    const [frames, setFrames] = useState([]);
    const [reference, setReference] = useState(null);
    const [heightRoi, setHeightRoi] = useState(3);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [maskPreview, setMaskPreview] = useState(null);

    useEffect(() => {
        LspivService.listFrames(jobId).then((list) => {
            setFrames(list);
            if (list.length > 0) setReference(list[0]);
        }).catch(() => {});
        LspivService.getSections(jobId).then((data) => {
            if (data && typeof data === 'object') setSections(data);
        }).catch(() => {});
    }, [jobId]);

    const updateSection = (key, field, value) => {
        setSections((s) => ({ ...s, [key]: { ...s[key], [field]: value } }));
    };

    const addSection = () => {
        const idx = Object.keys(sections).length + 1;
        setSections((s) => ({ ...s, [`section_${idx}`]: emptySection() }));
    };

    const removeSection = (key) => {
        setSections((s) => {
            const copy = { ...s };
            delete copy[key];
            return copy;
        });
    };

    const saveSections = async () => {
        setLoading(true); setError(null); setStatus(null);
        try {
            await LspivService.saveSections(jobId, sections);
            setStatus('Сечения сохранены');
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка сохранения сечений');
        } finally { setLoading(false); }
    };

    const createRoi = async () => {
        if (!reference) { setError('Выберите эталонный кадр'); return; }
        setLoading(true); setError(null); setStatus(null);
        try {
            await LspivService.createRoiMask(jobId, heightRoi, reference);
            setStatus('ROI-маска создана');
            setMaskPreview(LspivService.maskPreviewUrl(jobId, 'mask.png') + `?t=${Date.now()}`);
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка ROI-маски');
        } finally { setLoading(false); }
    };

    const compile = async () => {
        setLoading(true); setError(null); setStatus(null);
        try {
            await LspivService.compileMasks(jobId);
            setStatus('Итоговая маска собрана');
            setMaskPreview(LspivService.maskPreviewUrl(jobId, 'final_mask.png') + `?t=${Date.now()}`);
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка компиляции масок');
        } finally { setLoading(false); }
    };

    return (
        <Flex vertical gap={20}>
            <Title level={4} style={{ margin: 0 }}>
                <PartitionOutlined /> Поперечные сечения и маски
            </Title>
            <Text type="secondary">
                Задайте координаты сечений (левый и правый берег, длина в метрах). Затем сгенерируйте
                ROI-маску по высоте и соберите итоговую маску для PIV.
            </Text>

            {error && <Alert type="error" message={error} showIcon />}
            {status && <Alert type="success" message={status} showIcon />}

            <Flex gap={12} wrap="wrap" align="flex-end">
                <div style={{ minWidth: 240 }}>
                    <Text>Эталонный кадр</Text>
                    <Select
                        value={reference}
                        onChange={setReference}
                        style={{ width: '100%' }}
                        options={frames.map((f) => ({ value: f, label: f }))}
                    />
                </div>
                <div style={{ minWidth: 160 }}>
                    <Text>Height ROI (м)</Text>
                    <InputNumber value={heightRoi} onChange={setHeightRoi} min={1} style={{ width: '100%' }} />
                </div>
            </Flex>

            <Divider>Сечения</Divider>

            {Object.entries(sections).map(([key, s]) => (
                <Flex key={key} vertical gap={4} style={{ padding: 12, background: '#fafbfc', borderRadius: 8 }}>
                    <Flex justify="space-between" align="center">
                        <Text strong>{key}</Text>
                        <Button icon={<DeleteOutlined />} danger size="small" onClick={() => removeSection(key)} />
                    </Flex>
                    <Flex gap={12} wrap="wrap">
                        {['east_l', 'north_l', 'east_r', 'north_r', 'rw_length'].map((field) => (
                            <div key={field}>
                                <Text>{field}</Text>
                                <InputNumber
                                    value={s[field]}
                                    onChange={(v) => updateSection(key, field, v)}
                                    style={{ width: 120, display: 'block' }}
                                />
                            </div>
                        ))}
                    </Flex>
                </Flex>
            ))}

            <Flex gap={12}>
                <Button icon={<PlusOutlined />} onClick={addSection}>Добавить сечение</Button>
                <GreenLightButton onClick={saveSections} loading={loading}>Сохранить сечения</GreenLightButton>
            </Flex>

            <Divider>Маски</Divider>

            <Flex gap={12} wrap="wrap">
                <GreenLightButton onClick={createRoi} loading={loading} disabled={!reference}>
                    Создать ROI-маску
                </GreenLightButton>
                <Button type="primary" onClick={compile} loading={loading}>Собрать итоговую маску</Button>
            </Flex>

            {maskPreview && (
                <AntImage
                    src={maskPreview}
                    alt="mask preview"
                    style={{ maxHeight: 400, borderRadius: 8, background: '#000' }}
                />
            )}
        </Flex>
    );
};

export default SectionsStep;
